import React, { useState } from 'react';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import {
  ChevronDown,
  ChevronUp,
  PencilIcon,
  TrashIcon,
} from '../../../../ui/icons/icons';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import {
  deletePreScreenQuestion,
  editPreScreenQuestion,
} from '../../../../utils/api_service/pre_screen_api';
import {
  PreScreenQuestionBank,
  PreScreenQuestionType,
} from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { questionTypeOptions } from '../PreScreenSearch';

type PreScreenQuestionProps = {
  question: PreScreenQuestionBank;
  deleteQuestion: (questionId: string) => void;
  editQuestion: (editQuestion: PreScreenQuestionBank) => void;
  validateQuestion: (
    questionText: string,
    questionType: PreScreenQuestionType,
    choices: string[],
    defaultQuestion: boolean,
  ) => PreScreenQuestionBank | null;
};

export const PreScreenQuestion = ({
  question,
  deleteQuestion,
  editQuestion,
  validateQuestion,
}: PreScreenQuestionProps) => {
  const [edit, setEdit] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editQuestionType, setEditQuestionType] =
    useState<PreScreenQuestionType>(question.questionType);
  const [editQuestionText, setEditQuestionText] = useState<string>(
    question.question,
  );
  const [editChoices, setEditChoices] = useState<string[]>(
    question.choices && question.choices.length !== 0 ? question.choices : [''],
  );
  const [editDefaultQuestion, setEditDefaultQuestion] = useState<boolean>(
    question.defaultQuestion,
  );

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...editChoices];
    newChoices[index] = value;
    setEditChoices(newChoices);
  };

  const handleDeleteQuestion = async () => {
    try {
      if (question.id) {
        const response = await deletePreScreenQuestion(question.id);
        if (response.ok) {
          deleteQuestion(question.id);
        } else {
          toast.error('Error deleting question', { position: 'bottom-center' });
        }
      }
    } catch (error) {
      toast.error('Error deleting question', { position: 'bottom-center' });
    }
  };

  const handleSaveQuestion = async () => {
    try {
      if (question.id) {
        let newQuestionData = validateQuestion(
          editQuestionText,
          editQuestionType,
          editChoices,
          editDefaultQuestion,
        );

        if (!newQuestionData) return;
        newQuestionData = { ...newQuestionData, id: question.id };
        const response = await editPreScreenQuestion(newQuestionData);
        if (response.ok) {
          const result = await response.json();
          editQuestion(result);
          setEdit(false);
          setIsOpen(false);
        } else {
          toast.error('Error saving question', { position: 'bottom-center' });
        }
      }
    } catch (error) {
      toast.error('Error saving question', { position: 'bottom-center' });
    }
  };

  const handleEditQuestion = () => {
    setEdit(true);
    setIsOpen(true);
  };

  const questionTypeLabel =
    questionTypeOptions.find((option) => option.value === question.questionType)
      ?.label || '';

  return (
    <div
        className={`mb-1.5 w-full rounded-lg border bg-white transition-colors ${
          edit
            ? 'border-emerald-300'
            : question.defaultQuestion
              ? 'border-amber-300'
              : 'border-slate-200'
        }`}
    >
      <div
          className='flex items-center gap-2 px-3 py-2'
          onClick={() => setIsOpen((prev) => !prev)}
      >
        <button
            type='button'
            className='rounded-md p-1 text-slate-500 hover:bg-slate-100'
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
            aria-label={isOpen ? 'Collapse question' : 'Expand question'}
        >
          { isOpen ? (
            <ChevronUp className='h-4 w-4' />
          ) : (
            <ChevronDown className='h-4 w-4' />
          ) }
        </button>

        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium text-slate-800'>
            { question.question }
          </p>
          <div className='mt-0.5 flex items-center gap-2'>
            <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600'>
              { questionTypeLabel }
            </span>
            { question.defaultQuestion && (
              <span className='rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700'>
                Default
              </span>
            ) }
          </div>
        </div>

        <div className='flex items-center gap-1'>
          <button
              type='button'
              className='rounded-md p-1.5 text-teal-600 transition-colors hover:bg-teal-50'
              onClick={(e) => {
                e.stopPropagation();
                handleEditQuestion();
              }}
              aria-label='Edit question'
          >
            <PencilIcon className='h-4 w-4' />
          </button>
          <button
              type='button'
              className='rounded-md p-1.5 text-red-600 transition-colors hover:bg-red-50'
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteQuestion();
              }}
              aria-label='Delete question'
          >
            <TrashIcon className='h-4 w-4' />
          </button>
        </div>
      </div>

      { isOpen && (
        <div className='border-t border-slate-100 px-3 py-2.5'>
          { edit ? (
            <>
              <div className='flex w-full gap-3'>
                <div className='w-8/12'>
                  <TextInput
                      label='Question'
                      name='question'
                      value={editQuestionText}
                      type='text'
                      onChange={(e) => setEditQuestionText(e.target.value)}
                  />
                </div>
                <div className='w-4/12'>
                  <CustomSelect
                      label='Question Type'
                      name='questionType'
                      value={questionTypeOptions.find(
                        (option) => option.value === editQuestionType,
                      )}
                      options={questionTypeOptions}
                      onChange={(selectedOption: SelectOptions) =>
                        setEditQuestionType(
                        selectedOption.value as PreScreenQuestionType,
                        )
                      }
                  />
                </div>
              </div>

              { editQuestionType === 'multiple-choice' && (
                <div className='mt-1.5 w-full'>
                  <p className='text-xs font-semibold text-slate-500'>
                    Options:
                  </p>
                  { editChoices.map((choice, index) => (
                    <div key={index} className='flex w-full items-center gap-2'>
                      <div className='w-full'>
                        <TextInput
                            name='choice'
                            value={choice}
                            type='text'
                            onChange={(e) =>
                              handleChoiceChange(index, e.target.value)
                            }
                        />
                      </div>
                      <div>
                        { index === editChoices.length - 1 && (
                          <Button
                              onClick={() => setEditChoices([...editChoices, ''])}
                              variant='secondary'
                              label='Add Choice'
                              className='!border-slate-300 !text-slate-700'
                          />
                        ) }
                      </div>
                    </div>
                  )) }
                </div>
              ) }

              <div className='mt-1.5 flex w-full items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <input
                      type='checkbox'
                      className='h-4 w-4 accent-emerald-600'
                      onChange={() => {
                        setEditDefaultQuestion(!editDefaultQuestion);
                      }}
                      checked={editDefaultQuestion}
                  />
                  <div className='text-xs text-slate-600'>Set as default</div>
                </div>
                <div>
                  <Button
                      onClick={() => handleSaveQuestion()}
                      variant='primary'
                      label='Save'
                  />
                </div>
              </div>
            </>
          ) : (
            question.questionType === 'multiple-choice' && (
              <div className='flex flex-col gap-1'>
                <p className='text-xs font-semibold text-slate-500'>Options:</p>
                <div className='flex flex-wrap gap-1.5'>
                  { question.choices?.map((choice, idx) => (
                    <span
                        key={idx}
                        className='rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700'
                    >
                      { choice }
                    </span>
                  )) }
                </div>
              </div>
            )
          ) }
        </div>
      ) }
    </div>
  );
};
